import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { formatFieldValue } from '@/components/feature/GreenCertFieldConfig';
import {
  userApi,
  ratingApi,
  type PublicProfile,
  type RatingView,
  type PersonalSupply,
  type PersonalDemand,
} from '@/services/api';

function stars(score: number) {
  return '★'.repeat(score) + '☆'.repeat(5 - score);
}

function ListingRow({ item, kind }: { item: PersonalSupply | PersonalDemand; kind: 'supply' | 'demand' }) {
  return (
    <Link
      to={`/detail/${kind}/${item.id}`}
      className="flex items-center justify-between border border-background-200 rounded-lg p-4 hover:bg-background-100 transition-colors"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground-800 truncate">
          {formatFieldValue('projectType', item.projectType)} · {item.year}年度
        </p>
        <p className="text-xs text-foreground-400 mt-1">数量 {item.quantity} 张</p>
      </div>
      <span className="text-sm text-primary-600 font-medium shrink-0 ml-3">{item.price || '面议'}</span>
    </Link>
  );
}

export default function UserProfilePage() {
  const { id } = useParams<{ id: string }>();
  const uid = Number(id);
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [ratings, setRatings] = useState<RatingView[]>([]);

  useEffect(() => {
    if (!uid) return;
    userApi.publicProfile(uid).then(setProfile).catch(() => {});
    ratingApi.list(uid).then((r) => setRatings(r.list)).catch(() => {});
  }, [uid]);

  return (
    <>
      <Navbar />
      <div className="w-full bg-background-50 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-10">
          {profile && (
            <>
              <div className="bg-background-100 rounded-lg p-6 md:p-8 mb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {profile.companyName || '企业用户'}
                </h1>
                <p className="text-foreground-500 text-sm mt-2">
                  {profile.region}
                  {profile.region && ' · '}
                  {profile.role === 'supplier' ? '供应方' : '需求方'}
                </p>
                <p className="text-sm mt-3">
                  <span className="text-primary-600 font-bold text-lg">{profile.ratingAvg.toFixed(1)}</span>
                  <span className="text-foreground-400"> 分 · {profile.ratingCount} 条评价</span>
                </p>
              </div>

              {profile.supplies.length > 0 && (
                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">出售信息</h2>
                  <div className="flex flex-col gap-3">
                    {profile.supplies.map((s) => (
                      <ListingRow key={s.id} item={s} kind="supply" />
                    ))}
                  </div>
                </section>
              )}

              {profile.demands.length > 0 && (
                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">采购需求</h2>
                  <div className="flex flex-col gap-3">
                    {profile.demands.map((d) => (
                      <ListingRow key={d.id} item={d} kind="demand" />
                    ))}
                  </div>
                </section>
              )}

              <section>
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">用户评价</h2>
                {ratings.length === 0 && <p className="text-foreground-400 text-sm">暂无评价</p>}
                <div className="flex flex-col gap-4">
                  {ratings.map((r) => (
                    <div key={r.id} className="border border-background-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground-800">{r.raterName || '匿名用户'}</span>
                        <span className="text-primary-500">{stars(r.score)}</span>
                      </div>
                      {r.comment && <p className="text-sm text-foreground-600 mt-2">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
